<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import { reportBadNumberInput } from '@/utils/form-validity.utils';
  import type { IReservationShiftDto } from '@/api/services/reservations.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import V2ReservationArrivalTimesEditor from '@/components/v2/reservations/settings/V2ReservationArrivalTimesEditor.vue';
  import V2ReservationDepositScopeField from '@/components/v2/reservations/settings/V2ReservationDepositScopeField.vue';
  import V2ReservationSlotsPreview from '@/components/v2/reservations/settings/V2ReservationSlotsPreview.vue';
  import type { IPreviewShift } from '@/components/v2/reservations/settings/V2ReservationSlotsPreview.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { reservationDepositScopePatch } from '@/domain/reservations/reservation-deposit-scope';
  import {
    arrivalTimesValid,
    hoursValid as areHoursValid,
    sortArrivalTimes,
  } from '@/domain/reservations/reservation-preview';
  import { shiftsOverlap } from '@/domain/reservations/shift-overlaps';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    RESERVATION_DEFAULT_SLOT_INTERVAL_MIN,
    RESERVATION_SLOT_INTERVALS,
  } from '@/interfaces/IReservationSettings';
  import type {
    IReservationArrivalTime,
    IReservationSettings,
    IReservationSettingsConfiguration,
    IReservationShift,
    TReservationSlotInterval,
  } from '@/interfaces/IReservationSettings';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  type TTimesMode = 'grid' | 'fixed';
  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
    /** The General-pane draft, so the preview follows unsaved table-time changes. */
    settings: IReservationSettings;
    settingsDirty?: boolean;
    shift?: IReservationShift;
    /** Initial values of a new shift (the setup guide's proposals); ignored when editing. */
    prefill?: Pick<
      IReservationShiftDto,
      'name' | 'days' | 'from' | 'to' | 'slotIntervalMin'
    >;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const modalName = 'v2-reservation-shift';
  const { restaurantId, branchId } = props.configuration.settings;
  const name = ref(props.shift?.name ?? props.prefill?.name ?? '');
  const days = ref([...(props.shift?.days ?? props.prefill?.days ?? [])]);
  const from = ref(
    props.shift?.from.slice(0, 5) ?? props.prefill?.from ?? '12:00'
  );
  const to = ref(props.shift?.to.slice(0, 5) ?? props.prefill?.to ?? '15:00');
  const maxCovers = ref(props.shift?.maxCovers?.toString() ?? '');
  const depositRequired = ref<boolean | null>(
    props.shift?.depositRequired ?? null
  );
  const timesMode = ref<TTimesMode>(
    props.shift?.arrivalTimes ? 'fixed' : 'grid'
  );
  const arrivalTimes = ref<IReservationArrivalTime[]>(
    (props.shift?.arrivalTimes ?? []).map((row) => ({ ...row }))
  );
  const slotIntervalMin = ref<TReservationSlotInterval>(
    props.shift?.slotIntervalMin ??
      props.prefill?.slotIntervalMin ??
      RESERVATION_DEFAULT_SLOT_INTERVAL_MIN
  );
  const intervals = RESERVATION_SLOT_INTERVALS;
  const saving = ref(false);
  const submitted = ref(false);
  const error = ref('');
  const weekdays = Globals.days.filter((day) => day.val < 7);
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  function current() {
    return (
      alive &&
      restaurantStore.restaurant?.id === restaurantId &&
      restaurantStore.selectedBranch?.id === branchId
    );
  }
  watch(
    () => [restaurantStore.restaurant?.id, restaurantStore.selectedBranch?.id],
    () => {
      if (!current()) modals.hide(modalName);
    }
  );
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const hoursValid = computed(() => areHoursValid(from.value, to.value));
  const coversValid = computed(
    () =>
      maxCovers.value === '' ||
      (/^\d+$/.test(maxCovers.value) &&
        Number(maxCovers.value) >= 1 &&
        Number(maxCovers.value) <= 32767)
  );
  const modeItems = computed(() => [
    {
      id: 'grid',
      label: tr('reservations.shifts.form.modeGrid', {
        minutes: slotIntervalMin.value,
      }),
    },
    { id: 'fixed', label: tr('reservations.shifts.form.modeFixed') },
  ]);
  const arrivalsValid = computed(
    () =>
      timesMode.value === 'grid' ||
      arrivalTimesValid(from.value, to.value, arrivalTimes.value)
  );
  const previewShifts = computed<IPreviewShift[]>(() => [
    {
      key: 'draft',
      name: null,
      from: from.value,
      to: to.value,
      arrivalTimes: timesMode.value === 'fixed' ? arrivalTimes.value : null,
      slotIntervalMin: slotIntervalMin.value,
    },
  ]);
  function setTimesMode(value: string) {
    if (saving.value || (value !== 'grid' && value !== 'fixed')) return;
    timesMode.value = value;
    // Keep the rows in memory while on the grid so a mis-tap loses nothing.
    if (value === 'fixed' && !arrivalTimes.value.length && hoursValid.value)
      arrivalTimes.value = [{ time: from.value, maxCovers: null }];
  }
  function pickInterval(minutes: TReservationSlotInterval) {
    if (!saving.value) slotIntervalMin.value = minutes;
  }
  const overlap = computed(() => {
    if (!hoursValid.value) return '';
    const others = props.configuration.shifts.filter(
      (shift) => shift.id !== props.shift?.id
    );
    const day = weekdays.find(
      (candidate) =>
        days.value.includes(candidate.val) &&
        others.some((other) =>
          shiftsOverlap(
            { days: [candidate.val], from: from.value, to: to.value },
            other
          )
        )
    );
    return day
      ? tr('reservations.shifts.form.overlap', { day: tr(day.name) })
      : '';
  });
  function toggleDay(day: number) {
    days.value = days.value.includes(day)
      ? days.value.filter((value) => value !== day)
      : [...days.value, day];
  }
  const form = ref<HTMLFormElement | null>(null);
  async function save() {
    submitted.value = true;
    if (reportBadNumberInput(form.value)) return;
    if (
      saving.value ||
      !current() ||
      !name.value.trim() ||
      !days.value.length ||
      !hoursValid.value ||
      !coversValid.value ||
      !arrivalsValid.value ||
      overlap.value
    )
      return;
    saving.value = true;
    error.value = '';
    const dto = {
      restaurantId,
      branchId,
      name: name.value.trim(),
      days: days.value,
      from: from.value,
      to: to.value,
      maxCovers: maxCovers.value === '' ? null : Number(maxCovers.value),
      arrivalTimes:
        timesMode.value === 'fixed'
          ? sortArrivalTimes(from.value, arrivalTimes.value)
          : null,
      slotIntervalMin: slotIntervalMin.value,
      sort: props.shift?.sort ?? props.configuration.shifts.length,
      ...reservationDepositScopePatch(depositRequired.value, props.shift),
    };
    try {
      if (props.shift)
        await ReservationsService.updateShift(props.shift.id, dto);
      else await ReservationsService.createShift(dto);
      if (!current()) return;
      ui.showToast(AlertType.Success, tr('reservations.shifts.saved'));
      proxy.$parent?.$emit('saved');
      modals.hide(modalName);
    } catch (cause) {
      if (!current()) return;
      saving.value = false;
      error.value = tr('reservations.shifts.error');
      ui.serverError(cause, error.value);
    }
  }
</script>

<template>
  <form
    ref="form"
    class="reservation-shift-modal"
    novalidate
    @submit.prevent="save"
  >
    <header class="modal-header">
      <h2>
        {{
          $t(
            shift
              ? 'reservations.shifts.form.editTitle'
              : 'reservations.shifts.form.newTitle'
          )
        }}
      </h2>
    </header>
    <div class="modal-content">
      <PosUiField
        :label="$t('reservations.shifts.form.name')"
        input-id="reservation-shift-name"
        :error="
          submitted && !name.trim()
            ? $t('reservations.shifts.form.nameRequired')
            : undefined
        "
        required
      >
        <PosUiInput
          id="reservation-shift-name"
          v-model="name"
          :maxlength="32"
          :placeholder="$t('reservations.shifts.form.namePlaceholder')"
          :disabled="saving"
        />
      </PosUiField>
      <PosUiField
        :label="$t('reservations.shifts.form.days')"
        :error="
          submitted && !days.length
            ? $t('reservations.shifts.form.daysRequired')
            : undefined
        "
        required
      >
        <div class="day-options">
          <button
            v-for="day in weekdays"
            :key="day.val"
            type="button"
            class="day-option"
            :class="{ selected: days.includes(day.val) }"
            :aria-pressed="days.includes(day.val)"
            :disabled="saving"
            @click="toggleDay(day.val)"
          >
            {{ $t(day.name) }}
          </button>
        </div>
      </PosUiField>
      <div class="time-fields">
        <PosUiField
          :label="$t('reservations.shifts.form.from')"
          input-id="reservation-shift-from"
          required
          ><input
            id="reservation-shift-from"
            v-model="from"
            class="native-input"
            type="time"
            step="900"
            required
            :disabled="saving"
        /></PosUiField>
        <PosUiField
          :label="$t('reservations.shifts.form.to')"
          input-id="reservation-shift-to"
          required
          ><input
            id="reservation-shift-to"
            v-model="to"
            class="native-input"
            type="time"
            step="900"
            required
            :disabled="saving"
        /></PosUiField>
      </div>
      <p v-if="hoursValid && to < from" class="hint">
        {{ $t('reservations.shifts.crossesMidnight') }}
      </p>
      <p v-if="submitted && !hoursValid" class="error" role="alert">
        {{ $t('reservations.shifts.form.hoursInvalid') }}
      </p>
      <p v-if="overlap" class="warning" role="alert">{{ overlap }}</p>
      <PosUiField
        :label="$t('reservations.shifts.form.timesMode')"
        :hint="
          timesMode === 'fixed'
            ? $t('reservations.shifts.form.modeFixedHint')
            : undefined
        "
      >
        <div class="times-mode">
          <PosUiSegmented
            :value="timesMode"
            :items="modeItems"
            :aria-label="$t('reservations.shifts.form.timesMode')"
            block
            @input="setTimesMode"
          />
          <div
            v-if="timesMode === 'grid'"
            class="interval-chips"
            role="group"
            :aria-label="$t('reservations.shifts.form.interval')"
          >
            <span class="interval-label">{{
              $t('reservations.shifts.form.interval')
            }}</span>
            <div class="interval-options">
              <PosUiChip
                v-for="minutes in intervals"
                :key="minutes"
                :active="minutes === slotIntervalMin"
                :disabled="saving"
                @click="pickInterval(minutes)"
                >{{ $t('reservations.common.minutes', { minutes }) }}</PosUiChip
              >
            </div>
          </div>
          <V2ReservationArrivalTimesEditor
            v-if="timesMode === 'fixed'"
            v-model="arrivalTimes"
            :from="from"
            :to="to"
            :interval-min="slotIntervalMin"
            :disabled="saving"
            :show-errors="submitted"
          />
        </div>
      </PosUiField>
      <V2ReservationSlotsPreview
        v-if="hoursValid"
        :shifts="previewShifts"
        :settings="settings"
        :settings-dirty="settingsDirty"
        :limit="16"
      />
      <PosUiField
        :label="$t('reservations.shifts.form.maxCovers')"
        input-id="reservation-shift-covers"
        :error="
          submitted && !coversValid
            ? $t('reservations.shifts.form.maxCoversInvalid')
            : undefined
        "
        ><PosUiInput
          id="reservation-shift-covers"
          v-model="maxCovers"
          type="number"
          min="1"
          max="32767"
          step="1"
          inputmode="numeric"
          :disabled="saving"
      /></PosUiField>
      <V2ReservationDepositScopeField
        v-if="configuration.settings.depositsAvailable"
        v-model="depositRequired"
        :label="$t('reservations.shifts.form.deposit')"
        :settings="configuration.settings"
        :disabled="saving"
      />
      <p v-if="error" class="error" role="alert">{{ error }}</p>
    </div>
    <footer class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="modals.hide(modalName)"
        >{{ $t('cancel') }}</PosUiButton
      ><PosUiButton type="submit" :disabled="saving || !!overlap">{{
        $t('save')
      }}</PosUiButton>
    </footer>
  </form>
</template>

<style scoped lang="scss">
  .reservation-shift-modal {
    @apply tw-flex tw-min-h-0 tw-flex-col tw-bg-white;
    max-height: 90dvh;
    .modal-header {
      @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
      h2 {
        @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }
    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-p-5;
    }
    .modal-actions {
      @apply tw-flex tw-flex-none tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-4;
    }
    .day-options {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .day-option {
      @apply tw-min-h-11 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-xs tw-text-neutral-600;
      &.selected {
        @apply tw-border-neutral-800 tw-bg-neutral-800 tw-text-white;
      }
    }
    .time-fields {
      @apply tw-grid tw-grid-cols-2 tw-gap-3;
    }
    .times-mode {
      @apply tw-flex tw-flex-col tw-gap-3;
    }
    .interval-chips {
      @apply tw-flex tw-flex-col tw-gap-2;
      .interval-label {
        @apply tw-text-xs tw-text-neutral-500;
      }
      // Two tidy rows of three on phones, one row once the modal is wide enough.
      .interval-options {
        @apply tw-grid tw-grid-cols-3 tw-gap-2 sm:tw-flex sm:tw-flex-wrap;
      }
    }
    .native-input {
      @apply tw-min-h-11 tw-min-w-0 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900;
    }
    .hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .error {
      @apply tw-m-0 tw-text-sm tw-text-red-600;
    }
    .warning {
      @apply tw-m-0 tw-rounded-lg tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-800;
    }
  }
</style>
